import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, ArrowRight, ShoppingBag, Tag as TagIcon, SlidersHorizontal } from 'lucide-react';
import { useRestaurantCart } from '@happyspace/sdk/restaurant';
import { type MenuItem, type Tag } from '../data/menu';
import { useMenuData } from '../lib/useMenuData';
import { money, cn } from '../lib/util';
import { ModifierModal, type SelectedModifier } from '../components/ModifierModal';
import { Img } from '../components/Img';

function keyOf(item: MenuItem): string {
  return item.raw?._id ?? item.name;
}

/** Real photo for a dish — prefers the live SDK item's own photo, falls back to a
   curated static photo (item.img) when the SDK item has none. Never a placeholder. */
function imageOf(item: MenuItem): string | undefined {
  const src = item.raw?.image || item.raw?.imageUrl || item.img;
  return typeof src === 'string' && src.trim() ? src : undefined;
}

function hasModifiers(item: MenuItem): boolean {
  return Array.isArray(item.raw?.modifierSets) && item.raw.modifierSets.length > 0;
}

const DIET: Record<Tag, { label: string; color: string }> = {
  veg: { label: 'Veg', color: 'var(--color-leaf)' },
  egg: { label: 'Egg', color: 'var(--color-gold)' },
  nonveg: { label: 'Non-Veg', color: 'var(--color-spice)' },
};

/* Small square veg/non-veg marker used on the cards. */
function DietBadge({ tag }: { tag: Tag }) {
  const { label, color } = DIET[tag];
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className="inline-grid place-items-center"
        style={{ width: '0.95rem', height: '0.95rem', border: `1.5px solid ${color}`, borderRadius: 3 }}
      >
        <span style={{ width: '0.42rem', height: '0.42rem', borderRadius: 999, background: color }} />
      </span>
      <span className="font-semibold uppercase" style={{ fontSize: '0.68rem', letterSpacing: '0.14em', color }}>
        {label}
      </span>
    </span>
  );
}

export default function MenuPage() {
  const { sections } = useMenuData();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Menu – Parotta Express | Parottas, Curries, Dosas, Biryanis';
    document.querySelector('meta[name="description"]')?.setAttribute('content', 'Browse the full Parotta Express menu — hand-slapped parottas, Chettinad curries, dosas, biryanis, and authentic South Indian dishes. Order online for pickup or delivery in Coppell, TX.');
  }, []);
  const {
    items: cartItems,
    addItem,
    updateQuantity,
    removeItem,
    subtotal,
    total,
    itemCount,
  } = useRestaurantCart();

  const cartLineFor = (item: MenuItem) => {
    const k = keyOf(item);
    return cartItems.find((ci: any) => (ci.menuItemId ?? ci._id) === k || ci.title === item.name);
  };

  const [customizing, setCustomizing] = useState<MenuItem | null>(null);

  const increment = (item: MenuItem) => {
    if (hasModifiers(item)) {
      setCustomizing(item);
      return;
    }
    const line = cartLineFor(item);
    if (line) {
      updateQuantity(line.cartItemKey, line.quantity + 1);
    } else {
      addItem(item.raw || ({ _id: item.name, title: item.name, basePrice: item.price } as any), [], 1);
    }
  };

  const decrement = (item: MenuItem) => {
    const line = cartLineFor(item);
    if (!line) return;
    if (line.quantity <= 1) removeItem(line.cartItemKey);
    else updateQuantity(line.cartItemKey, line.quantity - 1);
  };

  const confirmModifiers = (selected: SelectedModifier[], qty: number) => {
    if (!customizing) return;
    addItem(
      customizing.raw || ({ _id: customizing.name, title: customizing.name, basePrice: customizing.price } as any),
      selected,
      qty,
    );
    setCustomizing(null);
  };

  const [active, setActive] = useState(sections[0]?.id);
  const activeSection = sections.find((s) => s.id === active) ?? sections[0];

  const railRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const rail = railRef.current;
    const chip = rail?.querySelector<HTMLElement>(`[data-chip="${active}"]`);
    if (rail && chip) {
      const left = chip.offsetLeft - rail.clientWidth / 2 + chip.clientWidth / 2;
      rail.scrollTo({ left, behavior: 'smooth' });
    }
  }, [active]);

  function selectCategory(id: string) {
    setActive(id);
    document.getElementById('menu-grid-top')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  const tax = Math.max(0, total - subtotal);

  return (
    <div style={{ background: 'var(--color-surface)' }} className="min-h-screen pb-28 lg:pb-16">
      {/* Mobile category rail */}
      <div
        className="lg:hidden sticky top-[4.4rem] z-30"
        style={{
          background: 'color-mix(in srgb, var(--color-surface) 92%, transparent)',
          backdropFilter: 'saturate(1.4) blur(12px)',
          borderBottom: '1px solid var(--color-line)',
        }}
      >
        <div ref={railRef} className="no-scrollbar flex gap-1.5 overflow-x-auto px-4 py-3">
          {sections.map((s) => (
            <button
              key={s.id}
              data-chip={s.id}
              onClick={() => selectCategory(s.id)}
              className="px-3.5 py-1.5 rounded-full whitespace-nowrap text-[0.85rem] font-medium transition-colors"
              style={
                active === s.id
                  ? { background: 'var(--color-ink)', color: 'var(--color-ink-inverse)' }
                  : { background: 'var(--color-card)', color: 'var(--color-ink-2)', border: '1px solid var(--color-line)' }
              }
            >
              {s.title} <span className="tnum opacity-60">{s.items.length}</span>
            </button>
          ))}
        </div>
      </div>

      <div
        className="mx-auto w-full grid gap-8 xl:gap-10 px-5 lg:px-8 pt-24 lg:pt-28
                   lg:grid-cols-[14rem_minmax(0,1fr)] xl:grid-cols-[15rem_minmax(0,1fr)_21rem]"
        style={{ maxWidth: '90rem' }}
      >
        {/* ── Left: category sidebar (desktop) ── */}
        <aside className="hidden lg:block">
          <div className="sticky top-28">
            <p className="eyebrow">Categories</p>
            <ul className="mt-4 space-y-1 relative pr-4">
              {/* orange accent rail */}
              <span
                className="absolute top-1 bottom-1 right-1 w-[3px] rounded-full"
                style={{ background: 'var(--color-accent)', opacity: 0.85 }}
                aria-hidden
              />
              {sections.map((s) => {
                const on = active === s.id;
                return (
                  <li key={s.id}>
                    <button
                      onClick={() => selectCategory(s.id)}
                      className={cn(
                        'w-full flex items-center justify-between gap-2 px-4 py-2.5 rounded-xl text-left transition-colors duration-200',
                      )}
                      style={
                        on
                          ? { background: 'var(--color-ink)', color: 'var(--color-ink-inverse)', fontWeight: 600 }
                          : { color: 'var(--color-ink-2)' }
                      }
                      onMouseEnter={(e) => {
                        if (!on) e.currentTarget.style.background = 'var(--color-card-2)';
                      }}
                      onMouseLeave={(e) => {
                        if (!on) e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      <span className="truncate text-[0.95rem]">{s.title}</span>
                      <span className="tnum text-[0.82rem]" style={{ opacity: on ? 0.7 : 0.5 }}>
                        {s.items.length}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </aside>

        {/* ── Center: dishes ── */}
        <section id="menu-grid-top" className="scroll-mt-28 min-w-0">
          <div className="flex items-baseline gap-3">
            <h1 className="font-display text-ink" style={{ fontSize: 'clamp(1.9rem, 4vw, 2.6rem)', lineHeight: 1 }}>
              {activeSection?.title}
            </h1>
            <span className="text-muted" style={{ fontSize: '1rem' }}>
              {activeSection?.items.length} {activeSection?.items.length === 1 ? 'dish' : 'dishes'}
            </span>
          </div>
          {activeSection?.blurb && (
            <p className="mt-2 max-w-2xl text-ink-2" style={{ lineHeight: 1.6 }}>
              {activeSection.blurb}
            </p>
          )}

          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {activeSection?.items.map((item) => {
              const line = cartLineFor(item);
              const qty = line?.quantity ?? 0;
              const photo = imageOf(item);
              return (
                <article
                  key={item.name}
                  className="card overflow-hidden flex flex-col"
                  style={{ borderRadius: 'var(--pe-radius)' }}
                >
                  {photo && (
                    <Img
                      src={photo}
                      alt={item.name}
                      className="w-full aspect-[4/3]"
                      imgClassName="hover:scale-[1.03] transition-transform duration-500"
                    />
                  )}
                  <div className="p-5 flex flex-col flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <DietBadge tag={item.tag} />
                    {item.dealPercent && (
                      <span
                        className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 font-bold uppercase"
                        style={{ background: 'var(--color-accent)', color: 'var(--color-on-accent)', fontSize: '0.68rem', letterSpacing: '0.06em' }}
                      >
                        <TagIcon size={11} strokeWidth={2.5} />
                        {item.dealPercent}% OFF
                      </span>
                    )}
                  </div>
                  <h3 className="font-display text-ink mt-2.5" style={{ fontSize: '1.28rem', lineHeight: 1.15 }}>
                    {item.name}
                  </h3>
                  {item.desc && (
                    <p
                      className="mt-1.5"
                      style={{ fontSize: '0.86rem', lineHeight: 1.5, color: 'var(--color-ink)', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                    >
                      {item.desc}
                    </p>
                  )}

                  <div className="mt-auto pt-5 flex items-center justify-between">
                    <span className="flex items-baseline gap-2">
                      <span className="tnum font-display text-ink" style={{ fontSize: '1.3rem' }}>
                        {money(item.price)}
                      </span>
                      {item.originalPrice && (
                        <span className="tnum" style={{ fontSize: '0.9rem', color: 'var(--color-faint)', textDecoration: 'line-through' }}>
                          {money(item.originalPrice)}
                        </span>
                      )}
                    </span>

                    {hasModifiers(item) ? (
                      <button
                        onClick={() => increment(item)}
                        aria-label={`Customize ${item.name}`}
                        className="inline-flex items-center gap-1.5 rounded-full px-5 py-2 font-semibold transition-all active:scale-95 hover:-translate-y-0.5"
                        style={{ border: '1.5px solid var(--color-line-2)', color: 'var(--color-ink)', fontSize: '0.92rem' }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = 'var(--color-ink)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = 'var(--color-line-2)';
                        }}
                      >
                        <SlidersHorizontal size={14} />
                        Customize
                        {qty > 0 && <span className="tnum opacity-60">· {qty}</span>}
                      </button>
                    ) : qty > 0 ? (
                      <div className="flex items-center gap-2.5">
                        <button
                          onClick={() => decrement(item)}
                          aria-label={`Decrease ${item.name}`}
                          className="grid place-items-center w-9 h-9 rounded-full transition-transform active:scale-90"
                          style={{ border: '1px solid var(--color-line-2)', color: 'var(--color-ink-2)' }}
                        >
                          <Trash2 size={15} />
                        </button>
                        <span className="tnum font-semibold w-4 text-center text-ink">{qty}</span>
                        <button
                          onClick={() => increment(item)}
                          aria-label={`Add another ${item.name}`}
                          className="grid place-items-center w-9 h-9 rounded-full transition-transform active:scale-90 hover:brightness-110"
                          style={{ background: 'var(--color-ink)', color: 'var(--color-ink-inverse)' }}
                        >
                          <Plus size={16} strokeWidth={2.4} />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => increment(item)}
                        aria-label={`Add ${item.name} to order`}
                        className="rounded-full px-6 py-2 font-semibold transition-all active:scale-95 hover:-translate-y-0.5"
                        style={{ border: '1.5px solid var(--color-line-2)', color: 'var(--color-ink)', fontSize: '0.92rem' }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = 'var(--color-ink)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = 'var(--color-line-2)';
                        }}
                      >
                        Add
                      </button>
                    )}
                  </div>
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        {/* ── Right: order summary (desktop) ── */}
        <aside className="hidden xl:block">
          <div className="sticky top-28">
            <div className="card overflow-hidden" style={{ borderRadius: 'var(--pe-radius-lg)', boxShadow: 'var(--pe-shadow)' }}>
              <div className="px-6 pt-6 pb-4">
                <h2 className="font-display text-ink" style={{ fontSize: '1.5rem' }}>Your order</h2>
                <p className="text-ink text-sm mt-0.5">
                  {itemCount > 0 ? `${itemCount} ${itemCount === 1 ? 'item' : 'items'}` : 'No items yet'}
                </p>
              </div>

              {!cartItems || cartItems.length === 0 ? (
                <div className="px-6 pb-8 flex flex-col items-center text-center gap-3">
                  <div className="grid place-items-center w-16 h-16 rounded-full" style={{ background: 'var(--color-card-2)' }}>
                    <ShoppingBag size={26} style={{ color: 'var(--color-faint)' }} />
                  </div>
                  <p className="text-sm text-muted max-w-[14rem]">
                    Add dishes from the menu and they’ll show up here.
                  </p>
                </div>
              ) : (
                <>
                  <ul className="px-6 divide-y" style={{ borderTop: '1px solid var(--color-line)', borderColor: 'var(--color-line)' }}>
                    {cartItems.map((item: any) => (
                      <li key={item.cartItemKey} className="py-4 flex items-center gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-ink text-[0.92rem] leading-snug">{item.title}</p>
                          <p className="tnum text-xs font-semibold mt-0.5" style={{ color: 'var(--color-accent-deep)' }}>{money(item.basePrice)} each</p>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <button
                            onClick={() => (item.quantity <= 1 ? removeItem(item.cartItemKey) : updateQuantity(item.cartItemKey, item.quantity - 1))}
                            aria-label="Decrease"
                            className="grid place-items-center w-7 h-7 rounded-full transition-transform active:scale-90"
                            style={{ border: '1px solid var(--color-line-2)', color: 'var(--color-ink-2)' }}
                          >
                            {item.quantity <= 1 ? <Trash2 size={13} /> : <span className="text-base leading-none pb-0.5">−</span>}
                          </button>
                          <span className="tnum text-sm font-semibold w-4 text-center text-ink">{item.quantity}</span>
                          <button
                            onClick={() => updateQuantity(item.cartItemKey, item.quantity + 1)}
                            aria-label="Increase"
                            className="grid place-items-center w-7 h-7 rounded-full transition-transform active:scale-90"
                            style={{ background: 'var(--color-ink)', color: 'var(--color-ink-inverse)' }}
                          >
                            <Plus size={13} strokeWidth={2.4} />
                          </button>
                        </div>
                        <span className="tnum font-display text-ink text-[0.95rem] w-16 text-right shrink-0">
                          {money((item.basePrice + (item.modifierTotal || 0)) * item.quantity)}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <div className="px-6 py-4 space-y-2" style={{ borderTop: '1px solid var(--color-line)' }}>
                    <div className="flex justify-between text-sm text-ink-2">
                      <span>Subtotal</span>
                      <span className="tnum">{money(subtotal)}</span>
                    </div>
                    <div className="flex justify-between text-sm text-ink-2">
                      <span>Tax</span>
                      <span className="tnum">{money(tax)}</span>
                    </div>
                  </div>

                  <div className="px-6 pt-4 pb-6" style={{ borderTop: '1px solid var(--color-line)' }}>
                    <div className="flex items-baseline justify-between">
                      <span className="font-display text-ink" style={{ fontSize: '1.3rem' }}>Total</span>
                      <span className="tnum font-display text-ink" style={{ fontSize: '1.5rem' }}>{money(total)}</span>
                    </div>
                    <button
                      onClick={() => navigate('/checkout')}
                      className="btn btn-primary w-full justify-center mt-4"
                      style={{ fontSize: '1rem' }}
                    >
                      Checkout
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </aside>
      </div>

      {/* ── Mobile / tablet sticky order bar ── */}
      {itemCount > 0 && (
        <div
          className="xl:hidden fixed bottom-0 inset-x-0 z-40 px-4 py-3"
          style={{ background: 'color-mix(in srgb, var(--color-surface) 94%, transparent)', backdropFilter: 'blur(10px)', borderTop: '1px solid var(--color-line)' }}
        >
          <button
            onClick={() => navigate('/checkout')}
            className="btn btn-primary w-full justify-between"
            style={{ paddingInline: '1.3rem' }}
          >
            <span className="tnum font-semibold">{itemCount} {itemCount === 1 ? 'item' : 'items'} · {money(total)}</span>
            <span className="inline-flex items-center gap-1.5">Checkout <ArrowRight size={16} /></span>
          </button>
        </div>
      )}

      {customizing && (
        <ModifierModal
          item={customizing}
          onClose={() => setCustomizing(null)}
          onConfirm={confirmModifiers}
        />
      )}
    </div>
  );
}
